import type { AddressRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';

type Args = {
  garageId?: number | null;
  serviceOrganizationId?: number | null;
  /** Used while garage/org location loads, or when neither id is available. */
  fallbackName?: string;
  fallbackAddress?: AddressRecord | null;
  enabled?: boolean;
};

/**
 * Resolves service-center name + address for stationary booking surfaces.
 * Prefers the garage site; falls back to the public service-organization location.
 */
export function useServiceCenterLocation({
  garageId,
  serviceOrganizationId,
  fallbackName = '',
  fallbackAddress,
  enabled = true
}: Args) {
  const { data: garage, isPending: isGaragePending } = noddiAsync.useGet({
    type: URLKeys.getGarage,
    input: { garageId: garageId! },
    queryConfig: {
      enabled: enabled && !!garageId
    }
  });

  const needsOrgFallback = !garageId || (!isGaragePending && !garage);
  const { data: serviceOrganizationLocation, isPending: isOrgLocationPending } = noddiAsync.useGet({
    type: URLKeys.getServiceOrganizationLocation,
    input: { pk: serviceOrganizationId! },
    queryConfig: {
      // Prefer garage; fall back to public org location when garage is missing or failed.
      enabled: enabled && !!serviceOrganizationId && needsOrgFallback
    }
  });

  const name = garage?.name ?? serviceOrganizationLocation?.name ?? fallbackName;
  const address = garage?.address ?? serviceOrganizationLocation?.address ?? fallbackAddress ?? undefined;
  const isPending =
    enabled && ((!!garageId && isGaragePending) || (!garageId && !!serviceOrganizationId && isOrgLocationPending));

  return { name, address, isPending };
}
